import {getCenter} from 'ol/extent';
import GeoJSON from 'ol/format/GeoJSON';
import {Circle} from 'ol/geom';
import {fromExtent as polygonFromExtent} from 'ol/geom/Polygon';
import {toRadians} from 'ol/math.js';
import {fromLonLat} from 'ol/proj';
import {register} from 'ol/proj/proj4';
import {getArea} from 'ol/sphere';
import proj4 from 'proj4';
import {trackEvent} from '@src/analytics';
import {GEO_PROJECTION, MAX_GEOMETRY_AREA} from '@src/constants';
import {SUPER_RES_AOI_OPTIMAL_AREA} from '@src/constants/analyze';

proj4.defs(
  'EPSG:3035',
  '+proj=laea +lat_0=52 +lon_0=10 +x_0=4321000 +y_0=3210000 +ellps=GRS80 +units=m +no_defs',
);
register(proj4);

const MAX_SIDE_LENGTH = Math.sqrt(MAX_GEOMETRY_AREA * 1e6);
const MAX_RADIUS = Math.sqrt((MAX_GEOMETRY_AREA * 1e6) / Math.PI);
const MIN_RADIUS = 10000;

function getMaxExtent(coords) {
  const half = MAX_SIDE_LENGTH / 2;
  return [
    coords[0] - half,
    coords[1] - half,
    coords[0] + half,
    coords[1] + half,
  ];
}

function getMinExtent(coords) {
  return [
    coords[0] - MIN_RADIUS,
    coords[1] - MIN_RADIUS,
    coords[0] + MIN_RADIUS,
    coords[1] + MIN_RADIUS,
  ];
}

export const MIN_AREA = Math.PI * MIN_RADIUS * MIN_RADIUS;

export function getMaxSquare(coords) {
  return polygonFromExtent(getMaxExtent(coords));
}

/**
 * Returns a square polygon representing the suggested AOI for super resolution
 * at the given center point, accounting for latitude-based Mercator projection distortion.
 * @param {import('ol/coordinate').Coordinate} centerDegrees - Center point as [longitude, latitude] in degrees (EPSG:4326)
 * @returns {import('ol/geom/Polygon').default} Square polygon in WGS84 (EPSG:4326)
 */
export function getSquareForSuperRes(centerDegrees) {
  const projectionFactor = Math.cos(toRadians(Math.abs(centerDegrees[1])));
  const center = fromLonLat(centerDegrees);
  const sideLengthInMeters =
    Math.sqrt(SUPER_RES_AOI_OPTIMAL_AREA) / projectionFactor;
  const extent = [
    center[0] - sideLengthInMeters / 2,
    center[1] - sideLengthInMeters / 2,
    center[0] + sideLengthInMeters / 2,
    center[1] + sideLengthInMeters / 2,
  ];
  const polygon = polygonFromExtent(extent);
  polygon.transform('EPSG:3857', 'EPSG:4326');
  return polygon;
}

export function getMinSquare(coords) {
  return polygonFromExtent(getMinExtent(coords));
}

export function getMaxCircle(coords) {
  return new Circle(coords, MAX_RADIUS);
}

export function getMinCircle(coords) {
  return new Circle(coords, MIN_RADIUS);
}

const GEOJSON = new GeoJSON();
export const toGeoJSON = (geom, reproject = false) =>
  GEOJSON.writeGeometryObject(
    geom,
    reproject
      ? {featureProjection: 'EPSG:3857', dataProjection: 'EPSG:4326'}
      : undefined,
  );

export const toEpsg4326Geometry = geometry => {
  const sourceCrs = geometry.crs?.properties?.name ?? GEO_PROJECTION;
  const transformedGeometry = GEOJSON.readGeometry(geometry, {
    dataProjection: sourceCrs,
    featureProjection: GEO_PROJECTION,
  });
  return GEOJSON.writeGeometryObject(transformedGeometry);
};

export const isGeometryTooLarge = area => area / MAX_GEOMETRY_AREA >= 1.1;

export const computeGeometryTooLarge = geometry => {
  const area = getArea(geometry, {projection: GEO_PROJECTION}) / 1e6;
  const geometryTooLarge = isGeometryTooLarge(area);
  return {geometryTooLarge, area};
};

export const createGeometryObj = geometry => {
  let newGeometry;
  let geometryTooLarge = true;
  let center;
  const formatGeoJSON = new GeoJSON();

  if (geometry) {
    const olGeom = formatGeoJSON.readGeometry(geometry);
    newGeometry = olGeom.simplify(0.00000001);
    const {geometryTooLarge: isGeometryTooLarge, area} =
      computeGeometryTooLarge(olGeom);
    geometryTooLarge = isGeometryTooLarge;

    if (geometryTooLarge) {
      trackEvent('Geometry defined - large AOI', {
        area,
        units: 'sqkm',
      });
    }

    center = getCenter(olGeom.getExtent());
    newGeometry = formatGeoJSON.writeGeometryObject(newGeometry, {
      decimals: 8,
    });
  } else {
    newGeometry = geometry;
  }

  return {newGeometry, geometryTooLarge, center};
};
